import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export function EventGallery({ images = [] }) {
  const [active, setActive] = useState(null);
  const items = (images || []).filter((item) => item?.src);

  useEffect(() => {
    if (active == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setActive((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, items.length]);

  if (!items.length) return null;

  const current = active != null ? items[active] : null;

  return (
    <section id="gallery" data-testid="event-gallery" className="py-20 md:py-24 bg-[#0A0A0A] text-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899]">Moments</p>
        <div className="mt-4 grid lg:grid-cols-12 gap-8 items-end">
          <h2 className="lg:col-span-8 font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
            From our <span className="italic text-[#EC4899]">events.</span>
          </h2>
          <p className="lg:col-span-4 text-white/65 text-lg leading-relaxed">
            A glimpse of the people, tables, and workshops that make Creative Flare feel like home. Tap a photo to look closer.
          </p>
        </div>

        <div className="cf-gallery mt-12">
          {items.map((item, i) => (
            <button
              key={item.id || item.src}
              type="button"
              data-testid={`gallery-thumb-${i}`}
              className={`cf-gallery-tile group ${i === 0 ? "cf-gallery-tile-hero" : ""}`}
              onClick={() => setActive(i)}
              aria-label={item.caption ? `Open photo: ${item.caption}` : `Open photo ${i + 1}`}
            >
              <img src={item.src} alt={item.caption || `Event photo ${i + 1}`} />
              <span className="cf-gallery-veil" />
              {item.caption ? <span className="cf-gallery-caption">{item.caption}</span> : null}
            </button>
          ))}
        </div>
      </div>

      {current && (
        <div
          className="cf-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Event photo"
          onClick={() => setActive(null)}
        >
          <button type="button" className="cf-lightbox-close" aria-label="Close" onClick={() => setActive(null)}>
            <X size={22} />
          </button>
          {items.length > 1 && (
            <>
              <button
                type="button"
                className="cf-lightbox-nav cf-lightbox-prev"
                aria-label="Previous photo"
                onClick={(e) => {
                  e.stopPropagation();
                  setActive((i) => (i - 1 + items.length) % items.length);
                }}
              >
                <ChevronLeft size={22} />
              </button>
              <button
                type="button"
                className="cf-lightbox-nav cf-lightbox-next"
                aria-label="Next photo"
                onClick={(e) => {
                  e.stopPropagation();
                  setActive((i) => (i + 1) % items.length);
                }}
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
          <figure
            className="cf-lightbox-stage"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              e.currentTarget.dataset.touchX = String(e.changedTouches[0].clientX);
            }}
            onTouchEnd={(e) => {
              const start = Number(e.currentTarget.dataset.touchX || 0);
              const dx = e.changedTouches[0].clientX - start;
              if (dx > 50) setActive((i) => (i - 1 + items.length) % items.length);
              if (dx < -50) setActive((i) => (i + 1) % items.length);
            }}
          >
            <img key={current.src} src={current.src} alt={current.caption || ""} className="cf-lightbox-image" />
            {current.caption ? <figcaption>{current.caption}</figcaption> : null}
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/45 mt-3">
              {active + 1} / {items.length}
            </p>
          </figure>
        </div>
      )}
    </section>
  );
}
